import { LinkRecordSchema, ObjectRecordSchema, type Id, type LinkRecord, type ObjectRecord, type Transaction } from '@vp/shared-types';
import { boxBounds, type Rect } from '../geometry';
import type { CanvasMetaExt, LoadOptions, LoadResult, LogQuery, ModuleStore, SaveBatch, StorageAdapter, StorageUsage, ModuleDataStore } from './adapter';
import { tileKey, TILE_SIZE, VpDatabase, type LogRow, type ObjectRow } from './db';
import { classifyStorageError, type StorageFailure } from './health';

export async function sha256Hex(data: ArrayBuffer | Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', data as BufferSource);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function stripRow(row: ObjectRow): ObjectRecord {
  const { _tile, ...rec } = row;
  void _tile;
  return rec as ObjectRecord;
}

function toRow(o: ObjectRecord): ObjectRow {
  const b = boxBounds(o);
  return { ...o, _tile: tileKey(b.x + b.w / 2, b.y + b.h / 2) };
}

function tilesFor(r: Rect, limit: number): string[] | null {
  const x0 = Math.floor(r.x / TILE_SIZE);
  const x1 = Math.floor((r.x + r.w) / TILE_SIZE);
  const y0 = Math.floor(r.y / TILE_SIZE);
  const y1 = Math.floor((r.y + r.h) / TILE_SIZE);
  if ((x1 - x0 + 1) * (y1 - y0 + 1) > limit) return null;
  const out: string[] = [];
  for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push(x + ':' + y);
  return out;
}

/** Collect attachment hashes referenced by an object (core images and module props). */
export function attachmentRefs(o: ObjectRecord): string[] {
  const out: string[] = [];
  const a = o.props['attachment'];
  if (typeof a === 'string' && a) out.push(a);
  const list = o.props['$attachments'];
  if (Array.isArray(list)) for (const h of list) if (typeof h === 'string') out.push(h);
  return out;
}

/** IndexedDB implementation to use instead of the browser's (the in-memory fallback). */
export interface IdbBackend {
  indexedDB: IDBFactory;
  IDBKeyRange: typeof IDBKeyRange;
}

export type StorageProbe = { ok: true } | { ok: false; failure: StorageFailure; error: unknown };

export class DexieStorage implements StorageAdapter {
  db: VpDatabase;
  /** True once the in-memory backend replaced the browser's IndexedDB. */
  memoryOnly = false;

  constructor(
    private readonly name = 'vp',
    backend?: IdbBackend,
  ) {
    this.db = new VpDatabase(name, backend);
    this.memoryOnly = !!backend;
  }

  /**
   * Open the database and write, read and delete one row: finds out up front whether data can be
   * kept (private windows and blocked storage fail here rather than on the first save).
   */
  async probe(): Promise<StorageProbe> {
    try {
      await this.db.open();
      await this.db.settings.put({ key: '__probe', value: Date.now() });
      const row = await this.db.settings.get('__probe');
      await this.db.settings.delete('__probe');
      if (!row) throw new Error('storage probe: written row not found');
      return { ok: true };
    } catch (e) {
      const kind = classifyStorageError(e);
      // whatever the cause, the database cannot be used: a probe failure that is not full/blocked means unavailable
      return { ok: false, failure: kind === 'failed' ? 'unavailable' : kind, error: e };
    }
  }

  /** Continue without persistence: everything lives in memory until the tab closes. */
  useMemoryBackend(backend: IdbBackend): void {
    try {
      this.db.close();
    } catch {
      /* never opened */
    }
    this.db = new VpDatabase(this.name + '-memory', backend);
    this.memoryOnly = true;
  }

  async listCanvases(): Promise<CanvasMetaExt[]> {
    const rows = await this.db.canvases.toArray();
    return rows.sort((a, b) => b.updatedAt - a.updatedAt) as CanvasMetaExt[];
  }

  getCanvas(id: Id): Promise<CanvasMetaExt | undefined> {
    return this.db.canvases.get(id) as Promise<CanvasMetaExt | undefined>;
  }

  async putCanvas(meta: CanvasMetaExt): Promise<void> {
    await this.db.canvases.put(meta);
  }

  async updateCanvas(id: Id, patch: Partial<CanvasMetaExt>): Promise<void> {
    await this.db.canvases.update(id, patch);
  }

  async deleteCanvas(id: Id): Promise<void> {
    const db = this.db;
    await db.transaction('rw', [db.canvases, db.objects, db.links, db.oplog, db.quarantine, db.settings, db.moduleData], async () => {
      await db.settings.delete('thumb:' + id);
      await db.moduleData.where('scope').equals(id).delete();
      await db.objects.where('canvasId').equals(id).delete();
      await db.links.where('canvasId').equals(id).delete();
      await db.oplog.where('canvasId').equals(id).delete();
      await db.quarantine.where('canvasId').equals(id).delete();
      await db.canvases.delete(id);
    });
  }

  async loadCanvas(id: Id, opts: LoadOptions = {}): Promise<LoadResult> {
    const db = this.db;
    const meta = (await db.canvases.get(id)) as CanvasMetaExt | undefined;
    if (!meta) throw new Error(`Canvas ${id} not found`);
    const chunk = opts.chunkSize ?? 5000;
    let quarantined = 0;
    let objectCount = 0;
    const loaded = new Set<Id>();
    const bad: { table: string; reason: string; data: unknown; key: unknown }[] = [];

    const validate = (rows: ObjectRow[]): ObjectRecord[] => {
      const ok: ObjectRecord[] = [];
      for (const r of rows) {
        if (loaded.has(r.id)) continue;
        const rec = stripRow(r);
        const res = ObjectRecordSchema.safeParse(rec);
        if (res.success && rec.canvasId === id) {
          ok.push(rec);
          loaded.add(rec.id);
        } else {
          bad.push({ table: 'objects', reason: res.success ? 'canvas mismatch' : res.error.issues[0]?.message ?? 'invalid', data: r, key: [r.canvasId, r.id] });
        }
      }
      objectCount += ok.length;
      return ok;
    };

    // stage 2: objects around the last view
    if (opts.view) {
      const tiles = tilesFor(opts.view, 64);
      if (tiles) {
        const rows = await db.objects.where('[canvasId+_tile]').anyOf(tiles.map((t) => [id, t])).toArray();
        const ok = validate(rows);
        if (ok.length) opts.onChunk?.(ok, 'visible');
      }
    }
    // stage 3: everything else in chunks, yielding to the UI between chunks
    let offset = 0;
    for (;;) {
      const rows = await db.objects.where('canvasId').equals(id).offset(offset).limit(chunk).toArray();
      if (rows.length === 0) break;
      offset += rows.length;
      const ok = validate(rows);
      if (ok.length) opts.onChunk?.(ok, 'rest');
      if (rows.length < chunk) break;
      await new Promise((r) => setTimeout(r, 0));
    }

    const links: LinkRecord[] = [];
    for (const l of await db.links.where('canvasId').equals(id).toArray()) {
      const res = LinkRecordSchema.safeParse(l);
      if (res.success) links.push(l);
      else bad.push({ table: 'links', reason: res.error.issues[0]?.message ?? 'invalid', data: l, key: [l.canvasId, l.id] });
    }

    // move corrupted records aside without deleting them (§10.4)
    if (bad.length) {
      quarantined = bad.length;
      await db.transaction('rw', [db.quarantine, db.objects, db.links], async () => {
        for (const b of bad) {
          await db.quarantine.add({ canvasId: id, table: b.table, ts: Date.now(), reason: b.reason, data: b.data });
          if (b.table === 'objects') await db.objects.delete(b.key as [Id, Id]);
          else await db.links.delete(b.key as [Id, Id]);
        }
      });
    }

    const applied = meta.appliedSeq ?? 0;
    const replayRows = await db.oplog.where('[canvasId+seq]').between([id, applied + 1], [id, Infinity]).toArray();
    const replay = replayRows.map((r) => r.tx);
    return { meta, links, replay, quarantined, objectCount };
  }

  async createCanvasWithContent(meta: CanvasMetaExt, objects: ObjectRecord[], links: LinkRecord[]): Promise<void> {
    const db = this.db;
    await db.transaction('rw', [db.canvases, db.objects, db.links], async () => {
      if (await db.canvases.get(meta.id)) throw new Error(`Canvas ${meta.id} already exists`);
      await db.objects.bulkPut(objects.map(toRow));
      await db.links.bulkPut(links);
      await db.canvases.put({ ...meta, objectCount: objects.length });
    });
  }

  async readCanvasContent(id: Id): Promise<{ objects: ObjectRecord[]; links: LinkRecord[] }> {
    const rows = await this.db.objects.where('canvasId').equals(id).toArray();
    const links = await this.db.links.where('canvasId').equals(id).toArray();
    return { objects: rows.map(stripRow), links };
  }

  async appendOps(canvasId: Id, txs: Transaction[]): Promise<number> {
    if (!txs.length) return 0;
    let last = 0;
    await this.db.transaction('rw', this.db.oplog, async () => {
      for (const tx of txs) last = (await this.db.oplog.add({ canvasId, ts: tx.ts, tx })) as number;
    });
    return last;
  }

  async saveBatch(b: SaveBatch): Promise<void> {
    const db = this.db;
    await db.transaction('rw', [db.objects, db.links, db.canvases, db.oplog], async () => {
      if (b.putObjects.length) await db.objects.bulkPut(b.putObjects.map(toRow));
      if (b.deleteObjects.length) await db.objects.bulkDelete(b.deleteObjects.map((id) => [b.canvasId, id] as [Id, Id]));
      if (b.putLinks.length) await db.links.bulkPut(b.putLinks);
      if (b.deleteLinks.length) await db.links.bulkDelete(b.deleteLinks.map((id) => [b.canvasId, id] as [Id, Id]));
      const patch: Partial<CanvasMetaExt> = { ...(b.meta ?? {}), updatedAt: Date.now() };
      if (b.appliedSeq !== undefined) patch.appliedSeq = b.appliedSeq;
      await db.canvases.update(b.canvasId, patch);
      // the checkpoint is written: the log up to it is not needed for recovery any more (older databases
      // that never pruned are cleaned by their first save)
      if (b.appliedSeq !== undefined) await db.oplog.where('[canvasId+seq]').between([b.canvasId, 0], [b.canvasId, b.appliedSeq], true, true).delete();
    });
  }

  async pruneOplog(canvasId: Id, keepLast: number): Promise<void> {
    const meta = (await this.db.canvases.get(canvasId)) as CanvasMetaExt | undefined;
    const applied = meta?.appliedSeq ?? 0;
    const keys = await this.db.oplog.where('[canvasId+seq]').between([canvasId, 0], [canvasId, applied + 1]).primaryKeys();
    if (keys.length > keepLast) await this.db.oplog.bulkDelete(keys.slice(0, keys.length - keepLast));
  }

  async putAttachment(blob: Blob, mime?: string): Promise<string> {
    const hash = await sha256Hex(await blob.arrayBuffer());
    if (!(await this.db.attachments.get(hash))) {
      await this.db.attachments.put({ hash, blob, mime: mime ?? blob.type, size: blob.size, createdAt: Date.now() });
    }
    return hash;
  }

  async getAttachment(hash: string): Promise<Blob | undefined> {
    return (await this.db.attachments.get(hash))?.blob;
  }

  async hasAttachment(hash: string): Promise<boolean> {
    return (await this.db.attachments.where('hash').equals(hash).count()) > 0;
  }

  /** Delete attachments that no object references (§10.5). */
  async gcAttachments(minAgeMs = 60 * 60 * 1000): Promise<number> {
    const referenced = new Set<string>();
    await this.db.objects.each((o) => {
      for (const h of attachmentRefs(o)) referenced.add(h);
    });
    const now = Date.now();
    const toDelete: string[] = [];
    await this.db.attachments.each((a) => {
      if (!referenced.has(a.hash) && now - a.createdAt > minAgeMs) toDelete.push(a.hash);
    });
    await this.db.attachments.bulkDelete(toDelete);
    return toDelete.length;
  }

  async getSetting<T>(key: string): Promise<T | undefined> {
    return (await this.db.settings.get(key))?.value as T | undefined;
  }

  async setSetting(key: string, value: unknown): Promise<void> {
    await this.db.settings.put({ key, value });
  }

  async appendLogs(rows: LogRow[]): Promise<void> {
    if (rows.length) await this.db.logs.bulkAdd(rows);
  }

  async queryLogs(q: LogQuery = {}): Promise<LogRow[]> {
    let coll = this.db.logs.where('ts').above(q.since ?? 0);
    if (q.level) {
      const lvl = q.level;
      coll = coll.and((r) => r.level === lvl);
    }
    const rows = await coll.reverse().limit(q.limit ?? 1000).toArray();
    return rows;
  }

  async pruneLogs(olderThan: number): Promise<number> {
    return this.db.logs.where('ts').below(olderThan).delete();
  }

  async clearLogs(): Promise<void> {
    await this.db.logs.clear();
  }

  readonly moduleData: ModuleDataStore = {
    get: (moduleId, scope, key) => this.db.moduleData.get([moduleId, scope, key]),
    put: async (row) => {
      await this.db.moduleData.put(row);
    },
    delete: async (moduleId, scope, key) => {
      await this.db.moduleData.delete([moduleId, scope, key]);
    },
    list: (moduleId, scope) => this.db.moduleData.where('[moduleId+scope]').equals([moduleId, scope]).toArray(),
    ofScope: (scope) => this.db.moduleData.where('scope').equals(scope).toArray(),
    deleteScope: async (moduleId, scope) => {
      await this.db.moduleData.where('[moduleId+scope]').equals([moduleId, scope]).delete();
    },
  };

  readonly modules: ModuleStore = {
    list: () => this.db.modules.toArray(),
    get: (id) => this.db.modules.get(id),
    put: async (row) => {
      await this.db.modules.put(row);
    },
    delete: async (id) => {
      await this.db.transaction('rw', [this.db.modules, this.db.moduleVersions], async () => {
        await this.db.modules.delete(id);
        await this.db.moduleVersions.where('id').equals(id).delete();
      });
    },
    versions: async (id) => (await this.db.moduleVersions.where('id').equals(id).toArray()).sort((a, b) => b.savedAt - a.savedAt),
    putVersion: async (row) => {
      await this.db.moduleVersions.put(row);
    },
    deleteVersion: async (key) => {
      await this.db.moduleVersions.delete(key);
    },
  };

  async usage(): Promise<StorageUsage> {
    const size = (v: unknown) => {
      try {
        return JSON.stringify(v)?.length ?? 0;
      } catch {
        return 0;
      }
    };
    const per = new Map<Id, { bytes: number; refs: Set<string> }>();
    const at = (id: Id) => per.get(id) ?? per.set(id, { bytes: 0, refs: new Set() }).get(id)!;
    await this.db.objects.each((o) => {
      const c = at(o.canvasId);
      c.bytes += size(o);
      for (const h of attachmentRefs(o as unknown as ObjectRecord)) c.refs.add(h);
    });
    await this.db.links.each((l) => (at(l.canvasId).bytes += size(l)));
    await this.db.oplog.each((r) => (at(r.canvasId).bytes += size(r.tx)));
    const sizes = new Map<string, number>();
    let attBytes = 0;
    await this.db.attachments.each((a) => {
      sizes.set(a.hash, a.size);
      attBytes += a.size;
    });
    const referenced = new Set<string>();
    for (const c of per.values()) for (const h of c.refs) referenced.add(h);
    let unused = 0;
    let unusedBytes = 0;
    for (const [h, s] of sizes) {
      if (referenced.has(h)) continue;
      unused++;
      unusedBytes += s;
    }
    let previewBytes = 0;
    await this.db.settings.where('key').startsWith('thumb:').each((r) => (previewBytes += r.value instanceof Blob ? r.value.size : 0));
    let logBytes = 0;
    await this.db.logs.each((r) => (logBytes += size(r)));
    let moduleBytes = 0;
    await this.db.modules.each((m) => (moduleBytes += m.archive?.size ?? 0));
    await this.db.moduleVersions.each((m) => (moduleBytes += m.archive?.size ?? 0));
    const canvases = [...per].map(([id, c]) => ({ id, bytes: c.bytes, attachments: [...c.refs].reduce((s, h) => s + (sizes.get(h) ?? 0), 0) }));
    return {
      canvases,
      canvasBytes: canvases.reduce((s, c) => s + c.bytes, 0),
      attachments: { count: sizes.size, bytes: attBytes, unused, unusedBytes },
      previewBytes,
      logBytes,
      moduleBytes,
    };
  }

  async clearCanvases(): Promise<void> {
    const db = this.db;
    await db.transaction('rw', [db.canvases, db.objects, db.links, db.oplog, db.attachments, db.quarantine, db.settings], async () => {
      await Promise.all([db.canvases.clear(), db.objects.clear(), db.links.clear(), db.oplog.clear(), db.attachments.clear(), db.quarantine.clear()]);
      await db.settings.where('key').startsWith('thumb').delete();
    });
  }

  async clearPreviews(): Promise<void> {
    await this.db.settings.where('key').startsWith('thumb').delete();
  }

  async estimate(): Promise<{ usage?: number; quota?: number }> {
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) return navigator.storage.estimate();
    return {};
  }

  close(): void {
    this.db.close();
  }
}
